/**
 * Performance Tab - UNIFIED DESIGN
 * Single view serving RM, AM, and IA personas without overwhelming
 * Progressive disclosure: Core metrics visible, details expandable
 */

import React from 'react';
import { RefreshCw } from 'lucide-react';
import {
  PerformanceMetrics,
  PerformanceAttribution,
  RollingReturns
} from '../../services/api/portfolioApiService';
import { usePortfolioPptxExport } from './hooks/usePortfolioPptxExport';
import {
  PerformanceChart,
  TimeRangeSelector,
  LoadingSpinner,
  EmptyState,
  ExportDropdown
} from './index';
import type { PerformanceDataPoint } from './index';
import type { AdditionalSeries } from './molecules/PerformanceChart';
import PerformanceSummaryCards from './molecules/PerformanceSummaryCards';
import PlainLanguageInsight from './molecules/PlainLanguageInsight';
import DualAttributionLayout from './molecules/DualAttributionLayout';
import RollingReturnsSection from './molecules/RollingReturnsSection';

interface Contributor {
  name: string;
  contribution: number;
}

type TimeRangeValue = '1d' | '1w' | '1m' | '3m' | '6m' | 'ytd' | '1y' | 'all';

const BENCHMARK_OPTIONS = [
  { code: 'SPI', label: 'SPI 20' },
  { code: 'SMI', label: 'SMI' },
  { code: 'SP500', label: 'S&P 500' },
  { code: 'DAX', label: 'DAX' },
] as const;

interface PerformanceTabProps {
  mode: 'global' | 'client';
  metrics: PerformanceMetrics | null;
  history?: any[];
  attribution?: PerformanceAttribution | null;
  rollingReturns?: RollingReturns[];
  rollingReturns12m?: RollingReturns[];
  topContributors?: Contributor[];
  bottomContributors?: Contributor[];
  portfolioSeries?: AdditionalSeries[];
  valueFormat?: 'percentage' | 'currency' | 'number';
  loading?: boolean;
  timeRange?: string;
  onTimeRangeChange?: (range: string) => void;
  benchmark?: string;
  onBenchmarkChange?: (benchmark: string) => void;
  clientId?: string;
}



const PerformanceTab: React.FC<PerformanceTabProps> = ({
  metrics,
  history,
  attribution,
  rollingReturns,
  rollingReturns12m,
  topContributors: providedTopContributors,
  bottomContributors: providedBottomContributors,
  portfolioSeries = [],
  valueFormat = 'percentage',
  loading = false,
  timeRange: externalTimeRange,
  onTimeRangeChange: externalOnTimeRangeChange,
  benchmark = 'SPI',
  onBenchmarkChange,
  clientId,
}) => {
  const [localTimeRange, setLocalTimeRange] = React.useState<TimeRangeValue>('ytd');
  const timeRange = (externalTimeRange as TimeRangeValue) || localTimeRange;
  const setTimeRange = externalOnTimeRangeChange || ((v: string) => setLocalTimeRange(v as TimeRangeValue));

  // Transform performance history for PerformanceChart component
  // Ensure each point has a 'value' field (chart requires it)
  const performanceData: PerformanceDataPoint[] = React.useMemo(() => {
    if (!history || history.length === 0) return [];

    // If backend already provides 'value', use as-is
    if (history[0]?.value !== undefined) {
      return history.map((h: any) => ({ ...h }));
    }

    // Fallback: normalize portfolio_value to % change from start
    const startVal = parseFloat(history[0]?.portfolio_value ?? 0);
    return history.map((h: any) => ({
      ...h,
      value: startVal > 0
        ? parseFloat((((parseFloat(h.portfolio_value ?? 0) / startVal) - 1) * 100).toFixed(4))
        : 0,
    }));
  }, [history]);

  // Merge 3m and 12m rolling returns by date (must be before early returns — Rules of Hooks)
  const rollingReturnsData = React.useMemo(() => {
    const map12m = new Map<string, number>();
    (rollingReturns12m || []).forEach(r => {
      map12m.set(r.date, r.returns || 0);
    });

    return (rollingReturns || []).map(r => ({
      date: r.date,
      return3m: r.returns || 0,
      return12m: map12m.get(r.date) ?? r.returns ?? 0,
    }));
  }, [rollingReturns, rollingReturns12m]);

  const { loading: exportLoading, runExport } = usePortfolioPptxExport({
    clientId,
    onCsv: undefined,
  });

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <LoadingSpinner size="lg" text="Loading performance data..." />
      </div>
    );
  }

  // No data state
  if (!metrics) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <EmptyState
          type="no-data"
          title="No Performance Data Available"
          description="Performance metrics will appear here once data is available."
        />
      </div>
    );
  }

  // TASK-281: NO MOCK FALLBACK - use real contributors only
  const topContributors = providedTopContributors || [];
  const bottomContributors = providedBottomContributors || [];

  // TASK-407: Export handler — PPTX via LLM pipeline. CSV is disabled here
  // (CompositionTab / ClientLevelView own the data CSV; PerformanceTab is
  // chart-centric and has no row data to dump).
  return (
    <div className="space-y-6">
      {/* Header with Export and Time Range */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <h3 className="text-xl font-bold text-gray-900">Performance</h3>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500 mr-1">Benchmark:</span>
              {BENCHMARK_OPTIONS.map(({ code, label }) => (
                <button
                  key={code}
                  onClick={() => onBenchmarkChange?.(code)}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                    benchmark === code
                      ? 'bg-[#002B45] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="w-px h-6 bg-gray-200" />
            <ExportDropdown
              onExport={runExport}
              loading={exportLoading}
              enableCsv={false}
            />
            <TimeRangeSelector
              value={timeRange}
              onChange={(value) => setTimeRange(value as typeof timeRange)}
            />
            <button
              onClick={() => window.location.reload()}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* SECTION 1: Performance Summary - Universal Language */}
      <PerformanceSummaryCards
        // TASK-872: `??` chains, not `||`, and every leg can now be null.
        //
        // The backend stopped coercing an unknown return to zero; this chain would have put
        // it straight back. `total_return` came from `_calculate_total_return`, which returned
        // 0 for "fewer than two history points" — so a client with no performance history
        // rendered a green +0.00% YTD beside "Comparison unavailable". Both ends are honest
        // now: the calculator returns null and this passes it through.
        portfolioReturn={
          timeRange === '1d'
            ? (metrics?.today_return ?? metrics?.period_return ?? null)
            : (metrics?.period_return ?? metrics?.ytd_return ?? metrics?.total_return ?? null)
        }
        benchmarkReturn={metrics?.benchmark_return ?? null}
        alpha={metrics?.alpha ?? null}
        period={timeRange === '1d' ? 'TODAY' : timeRange.toUpperCase()}
        benchmarkName={metrics?.benchmark_name ?? 'Benchmark'}
        twrYtdFactor={metrics?.twr_ytd_factor}
        twrStartingBaseMv={metrics?.twr_starting_base_mv}
        twrEndingBaseMv={metrics?.twr_ending_base_mv}
      />

      {/* Plain Language Insight - RM friendly, informative for all */}
      {/* Only show if we have real attribution data with non-zero values */}
      {attribution && (attribution.security_selection != null && attribution.security_selection !== 0 || attribution.asset_allocation != null && attribution.asset_allocation !== 0) && (
        <PlainLanguageInsight
          primaryDriver={(attribution.security_selection ?? 0) > (attribution.asset_allocation ?? 0) ? "security selection" : "asset allocation"}
          driverContribution={Math.max(attribution.security_selection ?? 0, attribution.asset_allocation ?? 0)}
          riskStatus={
            metrics?.volatility != null && metrics.volatility > 15 ? 'above-target'
            : metrics?.volatility != null && metrics.volatility < 5 ? 'below-target'
            : 'within-target'
          }
        />
      )}

      {/* SECTION 2: Performance Chart */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <PerformanceChart
          data={performanceData}
          title={portfolioSeries.length > 0
            ? "Client Performance vs Individual Portfolios"
            : "Portfolio vs Benchmark Performance"}
          chartType="line"
          showBenchmark={true}
          height={320}
          showLegend={true}
          additionalSeries={portfolioSeries}
          valueLabel="Client Total"
          valueFormat={valueFormat}
        />
      </div>

      {/* SECTION 3: What Drove Performance - Dual Attribution Layout */}
      {(attribution || topContributors.length > 0 || bottomContributors.length > 0) && (
        <div>
          <h4 className="text-lg font-semibold text-gray-900 mb-4">What Drove Performance</h4>
          <DualAttributionLayout
            strategyAttribution={attribution ? [
              { name: 'Security Selection', contribution: attribution.security_selection ?? 0 },
              { name: 'Asset Allocation', contribution: attribution.asset_allocation ?? 0 },
              { name: 'Currency', contribution: attribution.currency ?? 0 },
              { name: 'Other', contribution: attribution.other ?? 0 }
            ] : []}
            topContributors={topContributors}
            bottomContributors={bottomContributors}
          />
        </div>
      )}

      {/* Risk & Quality Metrics removed — shown in Risk tab */}
      {/* Historical Performance removed — duplicates summary cards above */}

      {/* Rolling Returns Analysis */}
      <RollingReturnsSection
        data={rollingReturnsData}
        defaultExpanded={false}
      />
    </div>
  );
};

export default PerformanceTab;
